import {
  Calendar,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Euro,
  History,
  Loader2,
  Package,
  Plus,
} from "lucide-react";
import type { Log } from "../../types";
import type { JobCategory } from "../../utils/jobCategories";

export interface AdminOverviewBudgetMonth {
  monthKey: string;
  label: string;
  totalCents: number;
  count: number;
}

export interface AdminOverviewProps {
  canViewJobs: boolean;
  canSeeJobsTab: boolean;
  canManageStock: boolean;
  canViewMainHistory: boolean;
  canCreateJobs: boolean;
  categoryCounts: Record<JobCategory, number>;
  budgetTotalCents: number;
  budgetCount: number;
  budgetByMonth: AdminOverviewBudgetMonth[];
  lowStockCount: number;
  logs: Log[];
  onClearLogs?: () => void;
  onOpenJobsCategory: (category: JobCategory) => void;
  onOpenPlanning: () => void;
  onOpenBudget: () => void;
  onOpenStock: () => void;
  onNewJob: () => void;
  labels: {
    title: string;
    subtitle: string;
    newJob: string;
    toSchedule: string;
    toScheduleHint: string;
    inProgress: string;
    inProgressHint: string;
    done: string;
    doneHint: string;
    planning: string;
    planningHint: string;
    lowStock: string;
    lowStockHint: string;
    lowStockOk: string;
    budget: string;
    seeDetail: string;
    noInvoiceYet: string;
    budgetJobsLine: (count: number) => string;
    monthlyEvolution: string;
    revenueTitle: string;
    caTotal: string;
    caThisMonth: string;
    avgTicket: string;
    invoicesCount: string;
    trendLabel: string;
    caChartHint: string;
    recentActivity: string;
    clear: string;
    noRecentActivity: string;
    noAccess: string;
  };
}

const cardBase =
  "rounded-2xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/10 p-5 md:p-6 transition-all duration-200 hover:shadow-lg dark:hover:shadow-xl cursor-pointer group text-left w-full";

export function AdminOverview({
  canViewJobs,
  canSeeJobsTab,
  canManageStock,
  canViewMainHistory,
  canCreateJobs,
  categoryCounts,
  budgetTotalCents,
  budgetCount,
  budgetByMonth,
  lowStockCount,
  logs,
  onClearLogs,
  onOpenJobsCategory,
  onOpenPlanning,
  onOpenBudget,
  onOpenStock,
  onNewJob,
  labels,
}: AdminOverviewProps) {
  const hasAnySection = canViewJobs || canManageStock || canViewMainHistory;
  const avgTicketCents = budgetCount > 0 ? Math.round(budgetTotalCents / budgetCount) : 0;
  const thisMonth = budgetByMonth.length > 0 ? budgetByMonth[budgetByMonth.length - 1] : null;
  const prevMonth = budgetByMonth.length > 1 ? budgetByMonth[budgetByMonth.length - 2] : null;
  const momPercent =
    thisMonth && prevMonth && prevMonth.totalCents > 0
      ? Math.round(((thisMonth.totalCents - prevMonth.totalCents) / prevMonth.totalCents) * 100)
      : null;
  const formatEuro = (cents: number) =>
    (cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">
            {labels.title}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">{labels.subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canViewJobs && canSeeJobsTab && (
            <button
              type="button"
              onClick={onOpenPlanning}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800/90 text-sm font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            >
              <Calendar size={18} strokeWidth={1.75} />
              {labels.planning}
            </button>
          )}
          {canCreateJobs && canSeeJobsTab && (
            <button
              type="button"
              onClick={onNewJob}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium shadow-lg shadow-blue-500/25 transition-colors"
            >
              <Plus size={18} strokeWidth={2} />
              {labels.newJob}
            </button>
          )}
        </div>
      </div>

      {canViewJobs && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
          <button
            type="button"
            disabled={!canSeeJobsTab}
            onClick={() => canSeeJobsTab && onOpenJobsCategory("to_schedule")}
            className={`${cardBase} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-500 dark:text-slate-400">{labels.toSchedule}</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white tabular-nums">
                  {categoryCounts.to_schedule}
                </p>
                <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">{labels.toScheduleHint}</p>
              </div>
              <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 group-hover:scale-105 transition-transform shrink-0">
                <CalendarClock size={22} strokeWidth={1.75} />
              </div>
            </div>
          </button>

          <button
            type="button"
            disabled={!canSeeJobsTab}
            onClick={() => canSeeJobsTab && onOpenJobsCategory("in_progress")}
            className={`${cardBase} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-500 dark:text-slate-400">{labels.inProgress}</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white tabular-nums">
                  {categoryCounts.in_progress}
                </p>
                <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">{labels.inProgressHint}</p>
              </div>
              <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 shrink-0">
                <Loader2 size={22} strokeWidth={1.75} className="group-hover:animate-spin" />
              </div>
            </div>
          </button>

          <button
            type="button"
            disabled={!canSeeJobsTab}
            onClick={() => canSeeJobsTab && onOpenPlanning()}
            className={`${cardBase} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-500 dark:text-slate-400">{labels.planning}</p>
                <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">{labels.planningHint}</p>
              </div>
              <div className="p-2.5 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 group-hover:scale-105 transition-transform shrink-0">
                <Calendar size={22} strokeWidth={1.75} />
              </div>
            </div>
          </button>

          <button
            type="button"
            disabled={!canSeeJobsTab}
            onClick={() => canSeeJobsTab && onOpenJobsCategory("done")}
            className={`${cardBase} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-500 dark:text-slate-400">{labels.done}</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white tabular-nums">
                  {categoryCounts.done}
                </p>
                <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">{labels.doneHint}</p>
              </div>
              <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 group-hover:scale-105 transition-transform shrink-0">
                <CheckCircle2 size={22} strokeWidth={1.75} />
              </div>
            </div>
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
        {canViewJobs && (
          <button type="button" onClick={onOpenBudget} className={cardBase}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0">
                  <Euro size={22} strokeWidth={1.75} />
                </div>
                <div className="min-w-0 text-left">
                  <p className="font-medium text-gray-900 dark:text-white">{labels.budget}</p>
                  <p className="text-sm text-gray-500 dark:text-slate-400 truncate">
                    {budgetCount === 0
                      ? labels.noInvoiceYet
                      : `${(budgetTotalCents / 100).toLocaleString("fr-FR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} € · ${labels.budgetJobsLine(budgetCount)}`}
                  </p>
                </div>
              </div>
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1 group-hover:gap-2 transition-all shrink-0">
                {labels.seeDetail} <ChevronRight size={16} />
              </span>
            </div>
          </button>
        )}

        {canManageStock && (
          <button type="button" onClick={onOpenStock} className={cardBase}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div
                  className={`p-2.5 rounded-xl shrink-0 ${
                    lowStockCount > 0
                      ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                      : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-slate-300"
                  }`}
                >
                  <Package size={22} strokeWidth={1.75} />
                </div>
                <div className="min-w-0 text-left">
                  <p className="font-medium text-gray-900 dark:text-white">{labels.lowStock}</p>
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    {lowStockCount > 0
                      ? `${lowStockCount} · ${labels.lowStockHint}`
                      : labels.lowStockOk}
                  </p>
                </div>
              </div>
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1 group-hover:gap-2 transition-all shrink-0">
                {labels.seeDetail} <ChevronRight size={16} />
              </span>
            </div>
          </button>
        )}
      </div>

      {canViewJobs && (
        <div className="rounded-2xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/10 p-4 md:p-6 shadow-sm overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{labels.revenueTitle}</p>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{labels.caChartHint}</p>
            </div>
            <button
              type="button"
              onClick={onOpenBudget}
              className="text-sm font-medium text-blue-600 dark:text-blue-400 inline-flex items-center gap-1 hover:gap-2 transition-all self-start sm:self-auto"
            >
              {labels.seeDetail} <ChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <div className="rounded-xl bg-gray-50/90 dark:bg-white/[0.04] border border-gray-100 dark:border-white/5 p-3.5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">{labels.caTotal}</p>
              <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white tabular-nums">
                {formatEuro(budgetTotalCents)} €
              </p>
            </div>
            <div className="rounded-xl bg-gray-50/90 dark:bg-white/[0.04] border border-gray-100 dark:border-white/5 p-3.5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">{labels.caThisMonth}</p>
              <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white tabular-nums">
                {formatEuro(thisMonth?.totalCents ?? 0)} €
              </p>
            </div>
            <div className="rounded-xl bg-gray-50/90 dark:bg-white/[0.04] border border-gray-100 dark:border-white/5 p-3.5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">{labels.avgTicket}</p>
              <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white tabular-nums">
                {formatEuro(avgTicketCents)} €
              </p>
            </div>
            <div className="rounded-xl bg-gray-50/90 dark:bg-white/[0.04] border border-gray-100 dark:border-white/5 p-3.5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">{labels.trendLabel}</p>
              <p
                className={`mt-1 text-xl font-semibold tabular-nums ${
                  momPercent == null
                    ? "text-gray-900 dark:text-white"
                    : momPercent >= 0
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-red-600 dark:text-red-400"
                }`}
              >
                {momPercent == null ? "—" : `${momPercent > 0 ? "+" : ""}${momPercent} %`}
              </p>
              <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-0.5">
                {labels.invoicesCount}: {budgetCount}
              </p>
            </div>
          </div>

          <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-3">{labels.monthlyEvolution}</p>
          <div className="relative h-[180px] w-full text-gray-900 dark:text-white">
            {(() => {
              const w = 640;
              const h = 150;
              const pad = { top: 10, right: 8, bottom: 22, left: 40 };
              const innerW = w - pad.left - pad.right;
              const innerH = h - pad.top - pad.bottom;
              const maxVal = Math.max(1, ...budgetByMonth.map((m) => m.totalCents));
              const points = budgetByMonth.map((m, i) => ({
                x: pad.left + (i / (budgetByMonth.length - 1 || 1)) * innerW,
                y: pad.top + innerH - (m.totalCents / maxVal) * innerH,
                ...m,
              }));
              const pathLine = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
              const pathArea = points.length
                ? `${pathLine} L ${points[points.length - 1].x} ${pad.top + innerH} L ${points[0].x} ${pad.top + innerH} Z`
                : "";
              const yTicks = [0, 0.5, 1].map((t) => ({
                y: pad.top + innerH - t * innerH,
                label: formatEuro(Math.round(maxVal * t)),
              }));
              return (
                <svg viewBox={`0 0 ${w} ${h + 16}`} className="w-full h-full min-h-[160px]" preserveAspectRatio="xMidYMid meet">
                  <defs>
                    <linearGradient id="overview-budget-area" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="rgb(16, 185, 129)" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="rgb(6, 182, 212)" stopOpacity="0.03" />
                    </linearGradient>
                    <linearGradient id="overview-budget-line" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgb(16, 185, 129)" stopOpacity="0.95" />
                      <stop offset="100%" stopColor="rgb(6, 182, 212)" stopOpacity="0.85" />
                    </linearGradient>
                  </defs>
                  {yTicks.map((tick) => (
                    <g key={tick.y}>
                      <line
                        x1={pad.left}
                        x2={pad.left + innerW}
                        y1={tick.y}
                        y2={tick.y}
                        stroke="currentColor"
                        strokeOpacity="0.08"
                        strokeWidth="1"
                      />
                      <text
                        x={pad.left - 6}
                        y={tick.y + 3}
                        textAnchor="end"
                        className="fill-gray-400 dark:fill-slate-500"
                        style={{ fontSize: "8px", fontFamily: "system-ui" }}
                      >
                        {tick.label}
                      </text>
                    </g>
                  ))}
                  <path d={pathArea} fill="url(#overview-budget-area)" />
                  <path
                    d={pathLine}
                    fill="none"
                    stroke="url(#overview-budget-line)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {points.map((p, i) => (
                    <circle
                      key={i}
                      cx={p.x}
                      cy={p.y}
                      r="3"
                      fill="white"
                      stroke="rgb(16, 185, 129)"
                      strokeWidth="1.5"
                      className="dark:fill-slate-800"
                    />
                  ))}
                  {budgetByMonth.map((m, i) => {
                    const x = pad.left + (i / (budgetByMonth.length - 1 || 1)) * innerW;
                    return (
                      <text
                        key={m.monthKey}
                        x={x}
                        y={h - 2}
                        textAnchor="middle"
                        className="fill-gray-400 dark:fill-slate-500"
                        style={{ fontSize: "8px", fontFamily: "system-ui" }}
                      >
                        {m.label}
                      </text>
                    );
                  })}
                </svg>
              );
            })()}
          </div>
        </div>
      )}

      {canViewMainHistory && (
        <div className="rounded-2xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/10 overflow-hidden">
          <div className="px-5 py-4 flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <History size={18} strokeWidth={1.75} /> {labels.recentActivity}
            </h3>
            {logs.length > 0 && onClearLogs && (
              <button
                type="button"
                onClick={onClearLogs}
                className="text-xs text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
              >
                {labels.clear}
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100 dark:divide-white/5">
            {logs.slice(0, 3).map((log) => (
              <div key={log.id} className="px-5 py-3 hover:bg-gray-50/50 dark:hover:bg-white/[0.03] transition-colors">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{log.action}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 truncate">
                  {new Date(log.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} · {log.details}
                </p>
              </div>
            ))}
            {logs.length === 0 && (
              <p className="px-5 py-6 text-center text-sm text-gray-500 dark:text-slate-400">{labels.noRecentActivity}</p>
            )}
          </div>
        </div>
      )}

      {!hasAnySection && (
        <div className="rounded-3xl bg-white dark:bg-slate-800/90 border border-gray-200/80 dark:border-white/[0.08] p-8 shadow-sm dark:shadow-none text-center">
          <p className="text-[15px] text-gray-500 dark:text-slate-400">{labels.noAccess}</p>
        </div>
      )}
    </div>
  );
}
